<template>
  <b-modal v-model="localVisible" title="Default Value Rules" @ok="onSave" @hide="onHide" size="xl">
    <template #title>
      Default Value Rules
      <modal-help topic="default-value-rules" />
    </template>
    <div class="dvre-container">
      <!-- Rule chain display -->
      <div v-if="localRules.length" class="dvre-rule-list">
        <template v-for="(rule, idx) in localRules" :key="rule.id">
          <div v-if="idx > 0" class="dvre-connector">
            <span class="dvre-connector-line"></span>
          </div>
          <div class="dvre-rule-item" :class="{ 'dvre-rule-editing': editingIndex === idx }">
            <button type="button" class="dvre-rule-open" @click="editRule(idx)">
            <span class="dvre-type-badge" :class="'dvre-type-' + rule.type">{{ rule.type.toUpperCase() }}</span>
            <span class="dvre-rule-body">
              <template v-if="rule.type !== 'else'">
                <span class="dvre-rule-conditions">
                  <template v-for="(cond, ci) in rule.conditions" :key="ci">
                    <span v-if="ci > 0" class="dvre-cond-join">{{ rule.conditionMatchMode === 'any' ? 'OR' : 'AND' }}</span>
                    <span class="dvre-cond-text">{{ conditionSummary(cond) }}</span>
                  </template>
                  <span v-if="!rule.conditions || !rule.conditions.length" class="dvre-cond-empty">(no conditions)</span>
                </span>
              </template>
              <span class="dvre-rule-arrow">→</span>
              <span class="dvre-rule-actions">
                <span v-for="(action, ai) in rule.actions" :key="ai" class="dvre-action-pill" :class="{ 'dvre-action-readonly': action.setReadonly }">
                  {{ actionSummary(action) }}
                </span>
                <span v-if="!rule.actions || !rule.actions.length" class="dvre-action-empty">no actions</span>
              </span>
            </span>
            </button>
            <button type="button" class="dvre-rule-remove" @click.stop="removeRule(idx)" aria-label="Remove rule">&times;</button>
          </div>
        </template>
      </div>

      <!-- Empty state -->
      <div v-if="!localRules.length && !isEditing" class="dvre-empty">
        No default value rules configured.
      </div>

      <!-- Edit form -->
      <div v-if="isEditing" class="dvre-edit-form">
        <div class="dvre-edit-title">{{ editingIndex !== null ? 'Edit' : 'New' }} Rule</div>

        <!-- Rule type: hidden when the chain leaves only one choice (a lone IF, or ELSE IF after ELSE) -->
        <div v-if="availableTypes.length > 1" class="dvre-form-row">
          <label class="dvre-form-label">Rule Type</label>
          <select v-model="editType" class="dvre-select">
            <option v-for="opt in availableTypes" :key="opt" :value="opt">{{ opt.toUpperCase() }}</option>
          </select>
        </div>

        <!-- Conditions (not for else) -->
        <template v-if="editType !== 'else'">
          <div class="dvre-form-section">
            <div class="dvre-section-header">
              <span class="dvre-section-title">Conditions</span>
              <div v-if="editConditions.length > 1" class="dvre-match-toggle">
                <button class="dvre-mt-btn" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">ALL</button>
                <button class="dvre-mt-btn" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">ANY</button>
              </div>
            </div>

            <div v-for="(cond, ci) in editConditions" :key="ci" class="dvre-condition-row">
              <select v-model="cond.source" @change="onCondSourceChange(cond)" class="dvre-select dvre-select-sm">
                <option value="self">Form field</option>
                <option value="field">Another field</option>
                <option value="related">Related field</option>
                <option value="none">Always (no condition)</option>
              </select>

              <select v-if="cond.source === 'self' || cond.source === 'field'" v-model="cond.field" @change="onCondFieldChange(cond)" class="dvre-select dvre-select-sm">
                <option value="" disabled>Select field...</option>
                <option v-for="col in directColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
              </select>

              <template v-if="cond.source === 'related'">
                <select v-model="cond.relationshipColumn" @change="onCondRelColChange(cond)" class="dvre-select dvre-select-sm">
                  <option value="" disabled>Relationship...</option>
                  <option v-for="rel in relationshipColumns" :key="rel.uniqueTitle" :value="rel.uniqueTitle">{{ rel.title || rel.uniqueTitle }}</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.relatedQuantifier" class="dvre-select dvre-select-sm dvre-quantifier">
                  <option value="any">any</option>
                  <option value="all">all</option>
                  <option value="none">none of</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.field" class="dvre-select dvre-select-sm">
                  <option value="" disabled>Field...</option>
                  <option v-for="col in getRelatedColumns(cond.relationshipColumn)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
                </select>
              </template>

              <template v-if="cond.source !== 'none'">
                <select v-model="cond.operator" class="dvre-select dvre-select-sm">
                  <option v-for="op in operatorsForDataType(cond.dataType || 'string')" :key="op.key" :value="op.key">{{ op.label }}</option>
                </select>

                <template v-if="!isNoValueOp(cond.operator)">
                  <!-- Relationship record picker -->
                  <template v-if="condFieldIsRelationship(cond)">
                    <div class="dvre-rel-cond-picker">
                      <div v-if="cond.relSelectedRecords && cond.relSelectedRecords.length" class="dvre-rel-tags">
                        <span v-for="rec in cond.relSelectedRecords" :key="rec.id" class="dvre-rel-tag">
                          {{ rec.displayValue }}
                          <button class="dvre-rel-tag-remove" @click="removeCondRelRecord(cond, rec.id)">&times;</button>
                        </span>
                      </div>
                      <div class="dvre-rel-search-wrap">
                        <input
                          type="text"
                          v-model="cond.relSearchQuery"
                          class="dvre-input dvre-input-sm"
                          placeholder="Search records..."
                          @input="onCondRelSearchInput(cond)"
                        />
                        <div v-if="cond.relSearchLoading" class="dvre-rel-loading">searching...</div>
                      </div>
                      <div v-if="cond.relSearchResults && cond.relSearchResults.length" class="dvre-rel-results">
                        <div
                          v-for="rec in cond.relSearchResults"
                          :key="rec.id"
                          class="dvre-rel-result-item"
                          @click="selectCondRelRecord(cond, rec)"
                        >{{ rec.displayValue || '(no value)' }}</div>
                      </div>
                    </div>
                  </template>
                  <input
                    v-else-if="cond.dataType === 'number'"
                    v-model.number="cond.value"
                    class="dvre-input dvre-input-sm"
                    type="number"
                    step="any"
                    placeholder="0"
                  />
                  <input
                    v-else-if="cond.dataType === 'datetime'"
                    v-model="cond.value"
                    class="dvre-input dvre-input-sm"
                    type="datetime-local"
                  />
                  <select
                    v-else-if="cond.dataType === 'checkbox'"
                    v-model="cond.value"
                    class="dvre-select dvre-select-sm"
                  >
                    <option value="true">Checked</option>
                    <option value="false">Unchecked</option>
                  </select>
                  <input
                    v-else
                    v-model="cond.value"
                    class="dvre-input dvre-input-sm"
                    type="text"
                    placeholder="value..."
                  />
                </template>
              </template>

              <button class="dvre-cond-remove" @click="removeCondition(ci)" title="Remove condition">&times;</button>
            </div>

            <button class="dvre-add-btn" @click="addCondition">+ add condition</button>
          </div>
        </template>

        <!-- Default Value Actions -->
        <div class="dvre-form-section">
          <div class="dvre-section-header">
            <span class="dvre-section-title">Default Value Actions</span>
          </div>

          <div v-for="(action, ai) in editActions" :key="ai" class="dvre-action-block">
            <div class="dvre-action-row">
              <label class="dvre-inline-label">Target field</label>
              <select v-model="action.field" @change="onActionFieldChange(action)" class="dvre-select dvre-select-sm">
                <option value="" disabled>Select field...</option>
                <option v-for="col in targetFieldOptions" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ targetFieldLabel(col) }}</option>
              </select>
              <button class="dvre-action-remove" @click="removeAction(ai)" title="Remove action">&times;</button>
            </div>

            <template v-if="action.field">
              <div class="dvre-action-row dvre-action-sub">
                <label class="dvre-checkbox-label">
                  <input type="checkbox" v-model="action.setValue" />
                  Set value
                </label>
              </div>

              <div v-if="action.setValue" class="dvre-action-row dvre-action-sub dvre-action-value-row">
                <!-- Relationship target: logged-in user, or a specific record -->
                <template v-if="isRelationshipTarget(action)">
                  <select
                    v-if="actionOffersLoggedInUser(action)"
                    :value="action.valueSource === 'loggedInUser' ? 'loggedInUser' : 'record'"
                    class="dvre-select dvre-select-sm"
                    @change="setRelationshipValueSource(action, $event.target.value)"
                  >
                    <option value="loggedInUser">The logged in user</option>
                    <option v-if="!isReverseUserTarget(action.field)" value="record">A specific record</option>
                  </select>
                  <p v-if="action.valueSource === 'loggedInUser'" class="dvre-user-hint">The person signed in through the login form.</p>
                  <div v-if="action.valueSource !== 'loggedInUser'" class="dvre-rel-picker">
                    <div v-if="action.relSelectedRecords && action.relSelectedRecords.length" class="dvre-rel-tags">
                      <span v-for="rec in action.relSelectedRecords" :key="rec.id" class="dvre-rel-tag">
                        {{ rec.displayValue }}
                        <button class="dvre-rel-tag-remove" @click="removeActionRelRecord(action, rec.id)">&times;</button>
                      </span>
                    </div>
                    <div class="dvre-rel-search-wrap">
                      <input
                        type="text"
                        v-model="action.relSearchQuery"
                        class="dvre-input dvre-input-sm dvre-input-wide"
                        placeholder="Search records..."
                        @input="onActionRelSearchInput(action)"
                      />
                      <div v-if="action.relSearchLoading" class="dvre-rel-loading">searching...</div>
                    </div>
                    <div v-if="action.relSearchResults && action.relSearchResults.length" class="dvre-rel-results">
                      <div
                        v-for="rec in action.relSearchResults"
                        :key="rec.id"
                        class="dvre-rel-result-item"
                        @click="selectActionRelRecord(action, rec)"
                      >{{ rec.displayValue || '(no value)' }}</div>
                    </div>
                  </div>
                </template>

                <!-- Non-relationship: static or from related record -->
                <template v-else>
                  <select v-model="action.valueSource" @change="onValueSourceChange(action)" class="dvre-select dvre-select-sm">
                    <option value="static">Static value</option>
                    <option value="related">From related record</option>
                  </select>

                  <!-- Static value: type-aware input -->
                  <template v-if="action.valueSource === 'static'">
                    <input
                      v-if="getColumnType(action.field) === 'number'"
                      v-model.number="action.staticValue"
                      class="dvre-input dvre-input-sm dvre-input-wide"
                      type="number"
                      step="any"
                      placeholder="0"
                    />
                    <input
                      v-else-if="getColumnType(action.field) === 'dateTime'"
                      v-model="action.staticValue"
                      class="dvre-input dvre-input-sm dvre-input-wide"
                      type="datetime-local"
                    />
                    <select
                      v-else-if="getColumnType(action.field) === 'checkbox'"
                      v-model="action.staticValue"
                      class="dvre-select dvre-select-sm"
                    >
                      <option value="true">Checked</option>
                      <option value="false">Unchecked</option>
                    </select>
                    <input
                      v-else
                      v-model="action.staticValue"
                      class="dvre-input dvre-input-sm dvre-input-wide"
                      type="text"
                      placeholder="Default value..."
                    />
                  </template>

                  <!-- From related record -->
                  <template v-if="action.valueSource === 'related'">
                    <select v-model="action.relationshipColumn" @change="onActionRelColChange(action)" class="dvre-select dvre-select-sm">
                      <option value="" disabled>Relationship...</option>
                      <option v-for="rel in relationshipColumns" :key="rel.uniqueTitle" :value="rel.uniqueTitle">{{ rel.title || rel.uniqueTitle }}</option>
                    </select>
                    <select v-if="action.relationshipColumn" v-model="action.relatedField" class="dvre-select dvre-select-sm">
                      <option value="" disabled>Field...</option>
                      <option v-for="col in getRelatedColumns(action.relationshipColumn)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
                    </select>
                  </template>
                </template>
              </div>

              <div class="dvre-action-row dvre-action-sub">
                <label class="dvre-checkbox-label">
                  <input type="checkbox" v-model="action.setReadonly" />
                  Make non-editable (read-only)
                </label>
              </div>
            </template>
          </div>

          <button class="dvre-add-btn" @click="addAction">+ add field action</button>
        </div>

        <!-- Edit buttons -->
        <div class="dvre-edit-buttons">
          <button class="dvre-btn dvre-btn-save" @click="saveRule">{{ editingIndex !== null ? 'Update' : 'Add' }} Rule</button>
          <button class="dvre-btn dvre-btn-cancel" @click="cancelEdit">Cancel</button>
        </div>
      </div>

      <!-- Add rule button -->
      <div v-if="!isEditing" class="dvre-add-rule-row">
        <button class="dvre-add-btn" @click="startAddRule">+ add rule</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { ruleChainTypes } from '../../lib/rule-chain-types.js'
import {
  loginProtectedUserLinks,
  isReverseTargetField,
  editorFieldForAction,
  normalizeLoggedInUserAction
} from '../../../../shared/logged-in-user-default.js'

const OPERATORS = {
  number: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: '>', label: 'greater than' },
    { key: '<', label: 'less than' },
    { key: '>=', label: 'greater or equal' },
    { key: '<=', label: 'less or equal' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  string: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'contains', label: 'contains' },
    { key: 'begins', label: 'starts with' },
    { key: 'ends', label: 'ends with' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  datetime: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'before', label: 'before' },
    { key: 'after', label: 'after' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  relationship: [
    { key: 'contains', label: 'contains' },
    { key: 'notContains', label: 'does not contain' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  checkbox: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  signature: [
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ]
}

let ruleIdCounter = 0

function generateRuleId () {
  return `dvrule_${Date.now()}_${++ruleIdCounter}`
}

export default {
  name: 'DefaultValueRuleEditor',
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    spreadsheet: { type: Object, default: null },
    allSpreadsheets: { type: Array, default: () => [] },
    applicationUniqueTitle: { type: String, default: '' },
    pages: { type: Array, default: () => [] },
    currentPageUniqueTitle: { type: String, default: '' }
  },
  data () {
    return {
      localVisible: false,
      localRules: [],
      isEditing: false,
      editingIndex: null,
      editType: 'if',
      editConditions: [],
      editActions: [],
      editMatchMode: 'all'
    }
  },
  computed: {
    allColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns
    },
    directColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns
    },
    relationshipColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns.filter(c => c.type === 'relationship')
    },
    availableTypes () {
      return ruleChainTypes(this.localRules, this.editingIndex)
    },
    userLinks () {
      return loginProtectedUserLinks({
        pages: this.pages,
        pageUniqueTitle: this.currentPageUniqueTitle,
        spreadsheet: this.spreadsheet,
        spreadsheets: this.allSpreadsheets
      })
    },
    targetFieldOptions () {
      const options = (this.allColumns || []).slice()
      const userSheet = (this.allSpreadsheets || []).find(sheet => sheet.uniqueTitle === this.userLinks.userSpreadSheet)
      const userTitle = userSheet ? (userSheet.title || userSheet.uniqueTitle) : 'User'

      for (const column of this.userLinks.reverse) {
        options.push({
          uniqueTitle: `user:${column.uniqueTitle}`,
          title: `${userTitle} · ${column.title || column.uniqueTitle}`,
          type: 'relationship',
          reverse: true
        })
      }

      return options
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
      if (val) {
        this.localRules = JSON.parse(JSON.stringify(this.rules || []))
        this.cancelEdit()
      }
    }
  },
  methods: {
    operatorsForDataType (dataType) {
      return OPERATORS[dataType] || OPERATORS.string
    },

    isNoValueOp (op) {
      return op === 'empty' || op === 'notEmpty'
    },

    getColumnDataType (fieldName) {
      if (!this.spreadsheet || !this.spreadsheet.columns) return 'string'
      const col = this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName)
      if (!col) return 'string'
      const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', formula: 'string', password: 'string', richText: 'string', relationship: 'relationship', checkbox: 'checkbox', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
      return typeMap[col.type] || 'string'
    },

    getRelatedColumns (relColUniqueTitle) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === relColUniqueTitle)
      if (!relCol || !relCol.relatedSpreadSheet) return []
      const relSS = this.allSpreadsheets.find(s => s.uniqueTitle === relCol.relatedSpreadSheet)
      if (!relSS || !relSS.columns) return []
      return relSS.columns.filter(c => c.type !== 'relationship')
    },

    condFieldIsRelationship (cond) {
      if (cond.dataType === 'relationship') return true
      if (cond.source === 'self' || cond.source === 'field') {
        return this.getColumnDataType(cond.field) === 'relationship'
      }
      return false
    },

    getCondRelatedSpreadSheet (cond) {
      const fieldName = cond.field
      if (!fieldName) return null
      const col = this.getColumnForField(fieldName)
      return col ? col.relatedSpreadSheet : null
    },

    onCondRelSearchInput (cond) {
      if (cond._relSearchTimeout) clearTimeout(cond._relSearchTimeout)
      cond._relSearchTimeout = setTimeout(() => this.searchCondRelRecords(cond), 250)
    },

    async searchCondRelRecords (cond) {
      const relSS = this.getCondRelatedSpreadSheet(cond)
      if (!relSS) return

      cond.relSearchLoading = true
      try {
        const headers = {}
        if (this.applicationUniqueTitle) {
          headers['application-unique-title'] = this.applicationUniqueTitle
        }
        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/data/${relSS}/search?q=${encodeURIComponent(cond.relSearchQuery || '')}`,
          { headers }
        )
        const data = await response.json()
        const selectedIds = new Set((cond.relSelectedRecords || []).map(r => r.id))
        cond.relSearchResults = data.filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        cond.relSearchResults = []
      } finally {
        cond.relSearchLoading = false
      }
    },

    selectCondRelRecord (cond, rec) {
      if (!cond.relSelectedRecords) cond.relSelectedRecords = []
      if (!cond.relSelectedRecords.find(r => r.id === rec.id)) {
        cond.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      cond.relSearchResults = (cond.relSearchResults || []).filter(r => r.id !== rec.id)
      cond.relSearchQuery = ''
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    removeCondRelRecord (cond, id) {
      cond.relSelectedRecords = (cond.relSelectedRecords || []).filter(r => r.id !== id)
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    conditionSummary (cond) {
      if (cond.source === 'none') return 'always'

      let fieldLabel = cond.field || '?'
      if (cond.source === 'related') {
        const q = cond.relatedQuantifier || 'any'
        const qLabel = q === 'none' ? 'none of' : q
        fieldLabel = `${qLabel} ${cond.relationshipColumn || '?'}.${cond.field || '?'}`
      }
      const opObj = this.operatorsForDataType(cond.dataType || 'string').find(o => o.key === cond.operator)
      const opLabel = opObj ? opObj.label : cond.operator
      if (this.isNoValueOp(cond.operator)) {
        return `${fieldLabel} ${opLabel}`
      }
      let displayValue = cond.value ?? ''
      if (this.condFieldIsRelationship(cond)) {
        const recs = cond.relSelectedRecords || []
        displayValue = recs.map(r => r.displayValue).join(', ') || '(none)'
      } else if (cond.dataType === 'checkbox') {
        displayValue = cond.value === 'true' ? 'Checked' : 'Unchecked'
      }
      return `${fieldLabel} ${opLabel} ${displayValue}`
    },

    getColumnType (fieldName) {
      const systemTypes = { id: 'number', createdAt: 'dateTime', updatedAt: 'dateTime' }
      if (systemTypes[fieldName]) return systemTypes[fieldName]
      if (!this.spreadsheet || !this.spreadsheet.columns) return 'text'
      const col = this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName)
      return col ? col.type : 'text'
    },

    getColumnForField (fieldName) {
      if (!this.spreadsheet || !this.spreadsheet.columns) return null
      return this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName) || null
    },

    actionSummary (action) {
      const fieldCol = this.allColumns.find(c => c.uniqueTitle === action.field)
      const fieldLabel = fieldCol ? (fieldCol.title || fieldCol.uniqueTitle) : (action.field || '?')

      const parts = []

      if (action.setValue) {
        if (action.valueSource === 'loggedInUser') {
          parts.push(`${this.loggedInUserActionLabel(action, fieldLabel)} = the logged in user`)
        } else if (fieldCol && fieldCol.type === 'relationship') {
          const recs = action.relSelectedRecords || []
          const recLabels = recs.map(r => r.displayValue).join(', ')
          parts.push(`${fieldLabel} = ${recLabels || '(no records)'}`)
        } else if (action.valueSource === 'related') {
          const relCol = this.relationshipColumns.find(c => c.uniqueTitle === action.relationshipColumn)
          const relLabel = relCol ? (relCol.title || relCol.uniqueTitle) : (action.relationshipColumn || '?')
          parts.push(`${fieldLabel} = ${relLabel}.${action.relatedField || '?'}`)
        } else {
          parts.push(`${fieldLabel} = "${action.staticValue ?? ''}"`)
        }
      }

      if (action.setReadonly) {
        parts.push(action.setValue ? '(read-only)' : `${fieldLabel} → read-only`)
      }

      return parts.length > 0 ? parts.join(' ') : `${fieldLabel}: no action`
    },

    // Condition source changes
    onCondSourceChange (cond) {
      cond.field = null
      cond.relationshipColumn = null
      cond.relatedSpreadsheet = null
      cond.relatedQuantifier = 'any'
      cond.operator = '='
      cond.value = ''
    },

    onCondFieldChange (cond) {
      cond.dataType = this.getColumnDataType(cond.field)
      cond.operator = this.operatorsForDataType(cond.dataType)[0]?.key || '='
    },

    onCondRelColChange (cond) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === cond.relationshipColumn)
      if (relCol) {
        cond.relatedSpreadsheet = relCol.relatedSpreadSheet
      }
      cond.field = null
    },

    targetFieldLabel (column) {
      const name = column.title || column.uniqueTitle
      if (column.reverse) return name
      if (column.type === 'relationship') return `${name} (rel)`
      return name
    },

    isReverseUserTarget (field) {
      return isReverseTargetField(field)
    },

    isRelationshipTarget (action) {
      return this.isReverseUserTarget(action.field) || this.getColumnType(action.field) === 'relationship'
    },

    actionOffersLoggedInUser (action) {
      if (!action || !this.userLinks.enabled) return false
      if (this.isReverseUserTarget(action.field)) {
        const name = action.field.slice('user:'.length)
        return this.userLinks.reverse.some(column => column.uniqueTitle === name)
      }
      return this.userLinks.forward.some(column => column.uniqueTitle === action.field)
    },

    setRelationshipValueSource (action, source) {
      action.valueSource = source
      if (source === 'loggedInUser') {
        action.relSelectedRecords = []
        action.relSearchQuery = ''
        action.relSearchResults = []
      }
    },

    loggedInUserActionLabel (action, fieldLabel) {
      if (action.linkFrom !== 'loggedInUser') return fieldLabel
      const sheetTitle = action.userSpreadSheet || this.userLinks.userSpreadSheet
      const userSheet = (this.allSpreadsheets || []).find(sheet => sheet.uniqueTitle === sheetTitle)
      const column = userSheet && (userSheet.columns || []).find(item => item.uniqueTitle === action.field)
      const userTitle = userSheet ? (userSheet.title || userSheet.uniqueTitle) : 'User'
      const columnTitle = column ? (column.title || column.uniqueTitle) : (action.field || '?')
      return `${userTitle} · ${columnTitle}`
    },

    onActionFieldChange (action) {
      const colType = this.getColumnType(action.field)
      action.staticValue = ''
      action.relationshipColumn = ''
      action.relatedField = ''
      action.relSelectedRecords = []
      action.relSearchQuery = ''
      action.relSearchResults = []
      if (this.actionOffersLoggedInUser(action)) {
        action.valueSource = 'loggedInUser'
      } else if (colType === 'relationship') {
        action.valueSource = 'record'
      }
    },

    onValueSourceChange (action) {
      action.staticValue = ''
      action.relationshipColumn = ''
      action.relatedField = ''
    },

    onActionRelColChange (action) {
      action.relatedField = ''
    },

    // Relationship record picker for relationship target fields
    onActionRelSearchInput (action) {
      if (action._relSearchTimeout) clearTimeout(action._relSearchTimeout)
      action._relSearchTimeout = setTimeout(() => this.searchActionRelRecords(action), 250)
    },

    async searchActionRelRecords (action) {
      const col = this.getColumnForField(action.field)
      if (!col || !col.relatedSpreadSheet) return

      action.relSearchLoading = true
      try {
        const headers = {}
        if (this.applicationUniqueTitle) {
          headers['application-unique-title'] = this.applicationUniqueTitle
        }
        const owner = this.spreadsheet && this.spreadsheet.uniqueTitle
        const response = await fetch(
          owner
            ? `${VUE_APP_PROXY_SERVER_URL}/data/${owner}/relationships/${col.uniqueTitle}/options?forLink=0&q=${encodeURIComponent(action.relSearchQuery || '')}`
            : `${VUE_APP_PROXY_SERVER_URL}/data/${col.relatedSpreadSheet}/search?q=${encodeURIComponent(action.relSearchQuery || '')}`,
          { headers }
        )
        const data = await response.json()
        const rows = Array.isArray(data) ? data : (data.options || [])
        const selectedIds = new Set((action.relSelectedRecords || []).map(r => r.id))
        action.relSearchResults = rows
          .map(row => ({ id: row.id, displayValue: row.displayValue || row.label || String(row.id) }))
          .filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        action.relSearchResults = []
      } finally {
        action.relSearchLoading = false
      }
    },

    selectActionRelRecord (action, rec) {
      if (!action.relSelectedRecords) action.relSelectedRecords = []
      const col = this.getColumnForField(action.field)
      if (!action.relSelectedRecords.find(r => r.id === rec.id)) {
        if (col && (col.relationshipType === 'oneToOne' || col.relationshipType === 'manyToOne')) {
          action.relSelectedRecords = [{ id: rec.id, displayValue: rec.displayValue }]
        } else {
          action.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
        }
      }
      action.relSearchResults = (action.relSearchResults || []).filter(r => r.id !== rec.id)
      action.relSearchQuery = ''
    },

    removeActionRelRecord (action, id) {
      action.relSelectedRecords = (action.relSelectedRecords || []).filter(r => r.id !== id)
    },

    // Rule CRUD
    startAddRule () {
      this.isEditing = true
      this.editingIndex = null
      this.editType = this.availableTypes[0]
      this.editConditions = [this.newCondition()]
      this.editActions = [this.newAction()]
      this.editMatchMode = 'all'
    },

    editRule (idx) {
      const rule = this.localRules[idx]
      this.isEditing = true
      this.editingIndex = idx
      this.editType = rule.type
      this.editConditions = JSON.parse(JSON.stringify(rule.conditions || [])).map(c => ({
        ...c,
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false,
        relSelectedRecords: c.relSelectedRecords || []
      }))
      this.editActions = JSON.parse(JSON.stringify(rule.actions || [])).map(a => ({
        ...a,
        field: editorFieldForAction(a),
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false,
        relSelectedRecords: a.relSelectedRecords || []
      }))
      this.editMatchMode = rule.conditionMatchMode || 'all'
    },

    saveRule () {
      // Strip transient search state from actions before saving
      const cleanActions = this.editActions
        .filter(a => a.field && (a.setValue || a.setReadonly))
        .map(a => {
          const clean = normalizeLoggedInUserAction(a, this.userLinks.userSpreadSheet)
          delete clean.relSearchQuery
          delete clean.relSearchResults
          delete clean.relSearchLoading
          delete clean._relSearchTimeout
          return clean
        })

      // Clean transient search state from conditions before saving
      const cleanConditions = (this.editType === 'else' ? [] : this.editConditions).map(c => {
        const clean = { ...c }
        delete clean.relSearchQuery
        delete clean.relSearchResults
        delete clean.relSearchLoading
        delete clean._relSearchTimeout
        return clean
      })

      const rule = {
        id: this.editingIndex !== null ? this.localRules[this.editingIndex].id : generateRuleId(),
        type: this.editType,
        conditions: cleanConditions,
        conditionMatchMode: this.editMatchMode,
        actions: cleanActions
      }

      if (this.editingIndex !== null) {
        this.localRules.splice(this.editingIndex, 1, rule)
      } else {
        const lastIdx = this.localRules.length - 1
        if (lastIdx >= 0 && this.localRules[lastIdx].type === 'else' && rule.type !== 'else') {
          this.localRules.splice(lastIdx, 0, rule)
        } else {
          this.localRules.push(rule)
        }
      }

      this.cancelEdit()
    },

    removeRule (idx) {
      this.localRules.splice(idx, 1)
      if (this.editingIndex === idx) {
        this.cancelEdit()
      } else if (this.editingIndex !== null && this.editingIndex > idx) {
        this.editingIndex--
      }
    },

    cancelEdit () {
      this.isEditing = false
      this.editingIndex = null
      this.editConditions = []
      this.editActions = []
    },

    // Condition/Action helpers
    newCondition () {
      return {
        source: 'none',
        field: null,
        operator: '=',
        value: '',
        dataType: 'string',
        relationshipColumn: null,
        relatedSpreadsheet: null,
        relatedQuantifier: 'any',
        relSelectedRecords: [],
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false
      }
    },

    addCondition () {
      this.editConditions.push(this.newCondition())
    },

    removeCondition (idx) {
      this.editConditions.splice(idx, 1)
    },

    newAction () {
      return {
        field: '',
        setValue: true,
        valueSource: 'static',
        staticValue: '',
        relationshipColumn: '',
        relatedField: '',
        setReadonly: false,
        relSelectedRecords: [],
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false
      }
    },

    addAction () {
      this.editActions.push(this.newAction())
    },

    removeAction (idx) {
      this.editActions.splice(idx, 1)
    },

    // Save/Cancel modal
    onSave () {
      if (this.isEditing) {
        this.saveRule()
      }
      this.$emit('update:rules', this.localRules)
      this.$emit('update:visible', false)
    },

    onHide () {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.dvre-container {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #141412;
  padding: 12px;
}

.dvre-rule-list { margin-bottom: 10px; }

.dvre-connector { display: flex; align-items: center; margin: 4px 0; padding: 0 4px; }
.dvre-connector-line { flex: 1; height: 1px; background: #2e2e2b; }

.dvre-rule-item {
  display: flex; align-items: center; gap: 8px; padding: 8px 10px;
  background: #1a1a17; border: 1px solid #2e2e2b; border-radius: 5px;
  cursor: pointer; transition: border-color 0.15s, background 0.12s;
}
.dvre-rule-item:hover { border-color: #474842; background: #1e1e1b; }
.dvre-rule-open {
  display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0;
  border: 0; background: transparent; color: inherit; font: inherit;
  text-align: left; padding: 0; cursor: pointer;
}
.dvre-rule-item.dvre-rule-editing { border-color: #9ad12b; }

.dvre-type-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 50px; height: 22px; border-radius: 4px;
  font-size: 0.65rem; font-weight: 700; letter-spacing: 0.06em;
  flex-shrink: 0; text-align: center; padding: 0 6px;
}
.dvre-type-if { background: #1e2e12; color: #9ad12b; }
.dvre-type-elseif { background: #2e2512; color: #d0a05b; }
.dvre-type-else { background: #251e30; color: #a05bd0; }

.dvre-rule-body {
  flex: 1; display: flex; align-items: center; gap: 6px; min-width: 0; flex-wrap: wrap;
}
.dvre-rule-conditions { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.dvre-cond-text { color: #e8e8e6; font-size: 0.78rem; font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace; }
.dvre-cond-join { color: #a8a8a4; font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; }
.dvre-cond-empty { color: #a8a8a4; font-size: 0.75rem; font-style: italic; }
.dvre-rule-arrow { color: #a8a8a4; font-size: 0.85rem; flex-shrink: 0; }
.dvre-rule-actions { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.dvre-action-pill {
  display: inline-flex; align-items: center; padding: 1px 8px; height: 20px;
  border-radius: 3px; font-size: 0.7rem; font-weight: 600;
  background: #1a2a3a; color: #5ba0d0; border: 1px solid #2a3a4a;
}
.dvre-action-pill.dvre-action-readonly { background: #2a1a1a; color: #d05b5b; border-color: #3a2a2a; }
.dvre-action-empty { color: #a8a8a4; font-size: 0.75rem; font-style: italic; }

.dvre-rule-remove, .dvre-cond-remove, .dvre-action-remove {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; background: transparent;
  border: 1px solid transparent; color: #a8a8a4; font-size: 16px;
  line-height: 1; border-radius: 3px; cursor: pointer;
  transition: all 0.15s; flex-shrink: 0;
}
.dvre-rule-remove:hover, .dvre-cond-remove:hover, .dvre-action-remove:hover {
  color: #f82471; border-color: #f82471; background: rgba(248, 36, 113, 0.1);
}

.dvre-empty {
  color: #a8a8a4; font-size: 0.82rem; font-style: italic; text-align: center; padding: 20px;
}

.dvre-edit-form {
  border: 1px solid #3a3a37; border-radius: 6px; background: #1a1a17;
  padding: 14px; margin-top: 10px;
}
.dvre-edit-title { color: #e8e8e6; font-size: 0.88rem; font-weight: 600; margin-bottom: 12px; }

.dvre-form-row { margin-bottom: 10px; }
.dvre-form-label {
  display: block; color: #7a7a78; font-size: 0.72rem; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px;
}
.dvre-inline-label {
  color: #7a7a78; font-size: 0.72rem; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap;
}

.dvre-form-section {
  border: 1px solid #2e2e2b; border-radius: 5px; padding: 10px;
  margin-bottom: 10px; background: #141412;
}
.dvre-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.dvre-section-title { color: #b0b0ae; font-size: 0.78rem; font-weight: 600; }

.dvre-match-toggle { display: flex; gap: 4px; }
.dvre-mt-btn {
  background: #1a1a17; border: 1px solid #2e2e2b; color: #7a7a78;
  font-size: 0.65rem; font-weight: 700; padding: 2px 8px;
  border-radius: 3px; cursor: pointer; transition: all 0.15s; letter-spacing: 0.06em;
}
.dvre-mt-btn:hover { border-color: #474842; color: #b0b0ae; }
.dvre-mt-btn.active { background: #1e2e12; border-color: #9ad12b; color: #9ad12b; }

.dvre-condition-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; flex-wrap: wrap; }

.dvre-action-block {
  border: 1px solid #2a2a27; border-radius: 4px; padding: 8px; margin-bottom: 8px; background: #181816;
}
.dvre-action-row { display: flex; align-items: center; gap: 6px; margin-bottom: 4px; flex-wrap: wrap; }
.dvre-action-sub { padding-left: 12px; }

.dvre-select {
  background: #1e1e1b; border: 1px solid #474842; color: #e8e8e6;
  font-size: 0.78rem; padding: 5px 8px; border-radius: 4px; outline: none; min-width: 100px;
}
.dvre-select:focus { border-color: #9ad12b; box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15); }
.dvre-select-sm { padding: 3px 6px; font-size: 0.75rem; }

.dvre-input {
  background: #1e1e1b; border: 1px solid #474842; color: #e8e8e6;
  font-size: 0.78rem; padding: 5px 8px; border-radius: 4px; outline: none; min-width: 80px;
}
.dvre-input:focus { border-color: #9ad12b; box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15); }
.dvre-input-sm { padding: 3px 6px; font-size: 0.75rem; }
.dvre-input-wide { min-width: 200px; flex: 1; }

.dvre-checkbox-label {
  display: flex; align-items: center; gap: 6px; color: #b0b0ae;
  font-size: 0.78rem; cursor: pointer; white-space: nowrap;
}

.dvre-add-btn {
  color: #9ad12b; background: transparent; border: 1px dashed #474842;
  padding: 5px 12px; border-radius: 4px; font-size: 0.75rem;
  cursor: pointer; transition: all 0.15s;
}
.dvre-add-btn:hover { color: #b8f046; background: #1e1e1b; border-color: #9ad12b; }

.dvre-add-rule-row { margin-top: 8px; }

.dvre-edit-buttons { display: flex; gap: 8px; margin-top: 12px; }
.dvre-btn {
  padding: 5px 16px; border-radius: 4px; font-size: 0.78rem; font-weight: 600;
  cursor: pointer; transition: all 0.15s; border: 1px solid;
}
.dvre-btn-save { background: #1e2e12; border-color: #9ad12b; color: #9ad12b; }
.dvre-btn-save:hover { background: #2a3e1a; }
.dvre-btn-cancel { background: transparent; border-color: #474842; color: #7a7a78; }
.dvre-btn-cancel:hover { border-color: #6a6a67; color: #b0b0ae; }

.dvre-quantifier { min-width: 80px; font-weight: 600; color: #d0a05b; }
.dvre-select option { background: #1e1e1b; color: #e8e8e6; }

.dvre-action-value-row { flex-direction: column; align-items: stretch; }
.dvre-user-hint { margin: 0; color: #8a8a86; font-size: 0.72rem; }

.dvre-rel-picker { width: 100%; }
.dvre-rel-cond-picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}
.dvre-rel-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.dvre-rel-tag {
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px;
  background: #1a2a3a; color: #5ba0d0; border: 1px solid #2a3a4a;
  border-radius: 3px; font-size: 0.72rem; font-weight: 600;
}
.dvre-rel-tag-remove {
  background: transparent; border: none; color: #a8a8a4; font-size: 14px;
  cursor: pointer; padding: 0 2px; line-height: 1;
}
.dvre-rel-tag-remove:hover { color: #f82471; }
.dvre-rel-search-wrap { position: relative; }
.dvre-rel-loading {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  color: #a8a8a4; font-size: 0.7rem; font-style: italic;
}
.dvre-rel-results {
  border: 1px solid #2e2e2b; border-radius: 4px; margin-top: 4px;
  max-height: 150px; overflow-y: auto; background: #1a1a17;
}
.dvre-rel-result-item {
  padding: 5px 8px; font-size: 0.75rem; color: #e8e8e6;
  cursor: pointer; border-bottom: 1px solid #2e2e2b;
}
.dvre-rel-result-item:last-child { border-bottom: none; }
.dvre-rel-result-item:hover { background: #1e2e12; color: #9ad12b; }
</style>
